/**
 * Superadmin-only feed of security findings (gitleaks, CodeQL, CSP/CORS,
 * dependency audits, drift checks). Surfaced in the notification bell so
 * superadmins see new alerts in real time without leaving the app.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useSuperAdmin } from "@/hooks/useSuperAdmin";
import { createRealtimeChannel, cleanupChannel } from "@/utils/realtime-factory";
import type { RealtimeChannel } from "@supabase/supabase-js";

export interface SecurityFindingAlert {
  id: string;
  workspace_id: string | null;
  source: string;
  severity: "low" | "medium" | "high" | "critical" | "info";
  status: "open" | "acknowledged" | "fixed" | "ignored";
  title: string;
  description: string | null;
  file_path: string | null;
  rule_id: string | null;
  commit_sha: string | null;
  detected_at: string;
  resolved_at: string | null;
  evidence?: unknown;
}

const MAX = 15;

export function useSecurityFindingsAlerts() {
  const { isSuperAdmin, loading: roleLoading } = useSuperAdmin();
  const [items, setItems] = useState<SecurityFindingAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const channelRef = useRef<RealtimeChannel | null>(null);

  const fetchItems = useCallback(async () => {
    if (!isSuperAdmin) {
      setItems([]);
      setLoading(false);
      return;
    }
    try {
      const { data, error } = await supabase
        .from("security_findings" as any)
        .select("*")
        .in("status", ["open", "acknowledged"])
        .order("detected_at", { ascending: false })
        .limit(MAX);
      if (error) throw error;
      setItems((data as any) || []);
    } catch (e) {
      console.error("[SecurityAlerts] fetch error", e);
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (roleLoading) return;
    fetchItems();

    if (!isSuperAdmin) return;

    if (channelRef.current) cleanupChannel(channelRef.current);
    const channel = createRealtimeChannel("security_findings:superadmin");
    channelRef.current = channel;
    channel
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "security_findings" },
        (payload) => {
          setItems((prev) => [payload.new as SecurityFindingAlert, ...prev].slice(0, MAX));
        }
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "security_findings" },
        (payload) => {
          const updated = payload.new as SecurityFindingAlert;
          setItems((prev) =>
            prev
              .map((it) => (it.id === updated.id ? updated : it))
              .filter((it) => it.status === "open" || it.status === "acknowledged")
          );
        }
      )
      .subscribe();

    return () => {
      cleanupChannel(channelRef.current);
      channelRef.current = null;
    };
  }, [isSuperAdmin, roleLoading, fetchItems]);

  const unreadCount = items.filter((i) => i.status === "open").length;

  return { items, unreadCount, loading, isSuperAdmin, refetch: fetchItems };
}
